import React, { useState, useEffect, useCallback } from 'react';
import { X, Shield, Info, Users, UserMinus } from 'lucide-react';
import { caseService } from '../services/api';
import MultiUserSelect from './common/MultiUserSelect';
import Notification from './common/Notification';

const MODE_INFO = {
  firm: {
    label: 'Entire Firm',
    description: 'All firm members can view this case.',
    icon: Users,
  },
  include: {
    label: 'Only Specific People',
    description: 'Only selected users (plus the assigned attorney and case creator) can view this case. New firm members will NOT have access.',
    icon: Shield,
  },
  exclude: {
    label: 'Everyone Except...',
    description: 'All firm members can view this case except selected users. New firm members will automatically have access.',
    icon: UserMinus,
  },
};

const CaseVisibilityModal = ({ caseId, caseName, onClose, onSaved }) => {
  const [mode, setMode] = useState('firm');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  // Load current visibility settings
  useEffect(() => {
    const load = async () => {
      try {
        const res = await caseService.getCaseVisibility(caseId);
        const data = res.data;
        setMode(data.visibility_mode);
        setSelectedUsers(
          data.rules.map(r => ({
            id: r.user_id,
            display_name: r.user_name,
            email: r.user_email,
          }))
        );
        if (data.rules.length > 0 && data.rules[0].reason) {
          setReason(data.rules[0].reason);
        }
      } catch (err) {
        console.error('Failed to load visibility settings:', err);
        setNotification({ type: 'error', message: 'Failed to load visibility settings' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [caseId]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await caseService.updateCaseVisibility(caseId, {
        visibility_mode: mode,
        user_ids: selectedUsers.map(u => u.id),
        reason: reason || null,
      });
      setNotification({ type: 'success', message: 'Visibility settings updated' });
      onSaved?.();
      setTimeout(onClose, 800);
    } catch (err) {
      console.error('Failed to update visibility:', err);
      setNotification({ type: 'error', message: err.response?.data?.detail || 'Failed to update visibility' });
    } finally {
      setSaving(false);
    }
  }, [caseId, mode, selectedUsers, reason, onClose, onSaved]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-yellow-600" />
            <h2 className="text-lg font-semibold text-gray-900">Case Visibility</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <>
              {caseName && (
                <p className="text-sm text-gray-500">
                  Configure who can view <span className="font-medium text-gray-700">{caseName}</span>
                </p>
              )}

              {/* Mode selection */}
              <div className="space-y-2">
                {Object.entries(MODE_INFO).map(([key, info]) => {
                  const Icon = info.icon;
                  return (
                    <label
                      key={key}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                        ${mode === key ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      <input
                        type="radio"
                        name="visibility_mode"
                        value={key}
                        checked={mode === key}
                        onChange={() => { setMode(key); if (key === 'firm') setSelectedUsers([]); }}
                        className="mt-1 accent-yellow-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5">
                          <Icon className="w-4 h-4 text-gray-600" />
                          <span className="font-medium text-sm text-gray-900">{info.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{info.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* User selection for include/exclude modes */}
              {mode !== 'firm' && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700">
                      {mode === 'include'
                        ? 'The assigned attorney and case creator always have access, even if not listed below.'
                        : 'Excluded users will not be able to see this case in any list, search, report, or dashboard.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {mode === 'include' ? 'Users who can view this case' : 'Users to exclude from this case'}
                    </label>
                    <MultiUserSelect
                      selectedUsers={selectedUsers}
                      onChange={setSelectedUsers}
                      placeholder={mode === 'include' ? 'Add users who should have access...' : 'Add users to exclude...'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g., Conflict of interest with opposing party"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 text-sm text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <Notification
        isOpen={!!notification}
        variant={notification?.type === 'error' ? 'error' : 'success'}
        message={notification?.message}
        onClose={() => setNotification(null)}
      />
    </div>
  );
};

export default CaseVisibilityModal;
